import * as React from 'react';
import { p as provideContext, O as ON_DISPATCH, b as createComponent, f as effect, s as signal, e as effect$1 } from './chunks/maverick-waoCsqaG.js';
import { c as camelToKebabCase, k as kebabToPascalCase } from './chunks/maverick-S-EudPoU.js';
import { c as createScope, d as getContext, e as isFunction, t as tick, f as isUndefined, i as isArray } from './chunks/maverick-OwX0SrFa.js';

const ReactScopeContext = React.createContext({ current: null });
ReactScopeContext.displayName = "Scope";
function WithScope(scope, ...children) {
  return React.createElement(ReactScopeContext.Provider, { value: scope }, ...children);
}
function useReactScope() {
  return React.useContext(ReactScopeContext).current;
}
function useReactContext(context) {
  const scope = useReactScope();
  return React.useMemo(() => getContext(context.id, scope), [scope]);
}
function createReactScopeProvider() {
  return ScopeProvider;
}
function createReactContextProvider(context, provide) {
  return class ContextProvider extends ScopeProvider {
    static {
      this.F = context;
    }
    static {
      this.T = provide;
    }
  };
}
class ScopeProvider extends React.Component {
  constructor(props, context) {
    super(props);
    this.d = {
      current: createScope()
    };
    if (context)
      context.append(this.d.current);
    const Ctor = this.constructor;
    if (Ctor.F)
      provideContext(Ctor.F, Ctor.T?.(), this.d.current);
  }
  static {
    this.contextType = ReactScopeContext;
  }
  render() {
    return WithScope(this.d, this.props?.children);
  }
}

function setRef(ref, value) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}
function composeRefs(...refs) {
  return (node) => refs.forEach((ref) => setRef(ref, node));
}

function createClientComponent(Component2, options) {
  const forwardComponent = React.forwardRef((props, forwardRef) => {
    let parentScopeRef = React.useContext(ReactScopeContext), scopeRef = React.useRef(null), stateRef = React.useRef();
    if (!stateRef.current) {
      const state2 = createInternalState(), component = initComponent(Component2, state2, props, parentScopeRef.current);
      state2.e = component;
      stateRef.current = state2;
      scopeRef.current = component.scope;
    }
    function onAttach() {
      let state2 = stateRef.current, scope = parentScopeRef.current;
      window.cancelAnimationFrame(state2.ea);
      state2.ea = -1;
      if (state2.e.$$.o) {
        const component = initComponent(Component2, state2, props, scope);
        state2.e = component;
        state2.Q = false;
        state2.da = false;
        scopeRef.current = component.scope;
      }
      if (state2.a) {
        attachToHost(state2, state2.a);
      }
      if (!state2.da) {
        setRef(forwardRef, state2.e);
        state2.da = true;
      }
      return () => detachFromHost(state2);
    }
    function onRefChange(el) {
      const state2 = stateRef.current;
      if (!state2.da) {
        state2.a = el;
        return;
      }
      window.cancelAnimationFrame(state2.K);
      state2.K = window.requestAnimationFrame(() => {
        const state3 = stateRef.current;
        state3.K = -1;
        if (state3.a === el)
          return;
        detachFromHost(state3);
        if (el)
          attachToHost(state3, el);
        state3.a = el;
      });
    }
    React.useEffect(() => {
      const state2 = stateRef.current;
      window.cancelAnimationFrame(state2.ea);
      state2.ea = -1;
      return function onDestroy() {
        if (!isFunction(props.children))
          return;
        window.cancelAnimationFrame(state2.K);
        state2.K = -1;
        window.cancelAnimationFrame(state2.E);
        state2.E = -1;
        window.cancelAnimationFrame(state2.ea);
        state2.ea = window.requestAnimationFrame(() => {
          state2.ea = -1;
          detachFromHost(state2);
          state2.e.$$.p();
          state2.e.$$[ON_DISPATCH] = null;
          state2.J = {};
          state2._ = {};
          scopeRef.current = null;
        });
      };
    }, []);
    React.useEffect(tick);
    let state = stateRef.current, { children, ...__props } = props, attrs = {}, prevPropNames = state.aa, newPropNames = Object.keys(__props);
    state.J = {};
    for (const name of [...prevPropNames, ...newPropNames]) {
      if (options.props.has(name)) {
        state.e.$props[name].set(
          // If the prop was removed we'll use the default value provided on Component creation.
          isUndefined(__props[name]) ? Component2.props?.[name] : __props[name]
        );
      } else if (options.events?.has(name) || options.eventsRE?.test(name)) {
        state.J[name] = __props[name];
      } else if (options.domEvents?.has(name) || options.domEventsRE?.test(name)) {
        let type = camelToKebabCase(name.slice(2));
        state._[type] = __props[name];
        if (!newPropNames.includes(name)) {
          state.a?.removeEventListener(type, state.ba);
          state.ca?.delete(type);
        } else if (state.a && !state.ca?.has(type)) {
          if (!state.ca)
            state.ca = /* @__PURE__ */ new Set();
          state.ca.add(type);
          state.a.addEventListener(type, state.ba);
        }
      } else {
        attrs[name] = __props[name];
      }
    }
    state.aa = newPropNames;
    return WithScope(
      scopeRef,
      React.createElement(AttachEffect, {
        effect: onAttach
      }),
      isFunction(children) ? children?.(
        {
          ...attrs,
          suppressHydrationWarning: true,
          ref: onRefChange
        },
        state.e
      ) : children
    );
  });
  forwardComponent.displayName = Component2.name + "Bridge";
  return forwardComponent;
}
function AttachEffect({ effect }) {
  React.useEffect(effect, []);
  return null;
}
const eventTypeToCallbackName = /* @__PURE__ */ new Map();
function createInternalState() {
  const state = {
    a: null,
    aa: [],
    J: {},
    _: {},
    K: -1,
    E: -1,
    ea: -1,
    Q: false,
    da: false,
    ca: null,
    ba(event) {
      const args = !isUndefined(event.detail) ? [event.detail, event] : [event];
      state._[event.type]?.(...args);
    }
  };
  return state;
}
function attachToHost(state, el) {
  if (state.a === el && state.Q)
    return;
  else if (state.Q)
    detachFromHost(state);
  if (state._) {
    if (!state.ca)
      state.ca = /* @__PURE__ */ new Set();
    for (const type of Object.keys(state._)) {
      if (state.ca.has(type))
        continue;
      el.addEventListener(type, state.ba);
      state.ca.add(type);
    }
  }
  state.e.$$.x(el);
  state.E = window.requestAnimationFrame(() => {
    state.e.$$.y();
    state.E = -1;
  });
  state.Q = true;
}
function detachFromHost(state) {
  if (!state.Q)
    return;
  window.cancelAnimationFrame(state.E);
  state.E = -1;
  state.e.$$.s();
  state.Q = false;
  if (state.a && state.ca) {
    for (const type of state.ca) {
      state.a.removeEventListener(type, state.ba);
    }
    state.ca.clear();
  }
}
function onDispatch(event) {
  let callbackProp = eventTypeToCallbackName.get(event.type), args = !isUndefined(event.detail) ? [event.detail, event] : [event];
  if (!callbackProp) {
    eventTypeToCallbackName.set(event.type, callbackProp = `on${kebabToPascalCase(event.type)}`);
  }
  this.J[callbackProp]?.(...args);
}
function initComponent(Component2, state, props, scope) {
  const component = createComponent(Component2, { props, scope });
  component.$$[ON_DISPATCH] = onDispatch.bind(state);
  component.$$.w();
  return component;
}

function useStateContext(state) {
  return useReactContext(state);
}

function useSignal(signal, key) {
  const [, scheduleReactUpdate] = React.useState();
  React.useEffect(() => {
    return effect(() => {
      signal();
      scheduleReactUpdate({});
    });
  }, [key ?? signal]);
  return signal();
}

function useSignalRecord($state) {
  const [, scheduleReactUpdate] = React.useState(), tracking = React.useRef(null);
  if (tracking.current == null) {
    tracking.current = {
      state: {},
      $update: signal({}),
      props: /* @__PURE__ */ new Set()
    };
  }
  React.useEffect(() => {
    let { state, $update, props } = tracking.current;
    return effect$1(() => {
      for (const prop of props) {
        const value = $state[prop]();
        state[prop] = isArray(value) ? [...value] : value;
      }
      $update();
      scheduleReactUpdate({});
    });
  }, [$state]);
  return React.useMemo(() => {
    let { state, $update, props } = tracking.current, scheduledUpdate = false;
    props.clear();
    return new Proxy(state, {
      get(_, prop) {
        if (!props.has(prop) && prop in $state) {
          props.add(prop);
          const value = $state[prop]();
          state[prop] = isArray(value) ? [...value] : value;
          if (!scheduledUpdate) {
            $update.set({});
            scheduledUpdate = true;
            queueMicrotask(() => scheduledUpdate = false);
          }
        }
        return state[prop];
      },
      set(_, prop, newValue) {
        if (!(prop in $state))
          state[prop] = newValue;
        return true;
      }
    });
  }, [$state]);
}

function createReactComponent(Component, options) {
  {
    return createClientComponent(Component, {
      props: new Set(Object.keys(Component.props || {})),
      events: new Set(options?.events),
      eventsRE: options?.eventsRegex,
      domEvents: options?.domEvents,
      domEventsRE: options?.domEventsRegex
    });
  }
}

export { ReactScopeContext, WithScope, composeRefs, createReactComponent, createReactContextProvider, createReactScopeProvider, setRef, useReactContext, useReactScope, useSignal, useSignalRecord, useStateContext };
